import React, { useRef, useEffect } from 'react';
import { ROUTES } from 'global/constants';
import { NavLink } from 'react-router-dom';
import { logout } from 'auth/actions';
import { useSelector } from 'react-redux';
import { getUsername, getUserFullName } from 'auth/selectors';
import { WrappedMessage } from 'utils';
import messages from '../../../displayMessages';
import authMessages from 'ui/components/displayMessages';
import { Button, Icon } from 'ui/components';
import { redirectToLogin, redirectToLxSignUp } from 'auth/actions';
import { UserAvatar } from 'user/components';

interface MobileNavbarProps {
  setSidebarOpen: () => void;
  sidebarOpen: boolean;
}

const MobileNavbar: React.FC<MobileNavbarProps> = ({
  setSidebarOpen,
  sidebarOpen,
}) => {
  const loggedInUsername = useSelector(getUsername);
  const fullName = useSelector(getUserFullName);
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setSidebarOpen();
      }
    };

    if (sidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen]);

  return (
    <div className='mobile-navigation-container' data-testid='mobile-navbar'>
      <div className='mobile-sidebar' ref={sidebarRef}>
        {!loggedInUsername ? (
          <div className='mobile-sidebar-header-logged-out'>
            <img
              className='logo'
              alt='CareerXplorer'
              title='CareerXplorer'
              src='/assets/images/career-explorer/career-xplorer-logo-navy.svg'
            />
          </div>
        ) : (
          <div className='mobile-sidebar-header-logged-in'>
            <div className='left-box'>
              <NavLink
                to={ROUTES.Users.PROFILE_EDIT_SLUG(loggedInUsername)}
                onClick={() => {
                  setSidebarOpen();
                }}
              >
                <UserAvatar username={loggedInUsername} />
              </NavLink>
            </div>
            <div className='flex-column right-box'>
              <div>
                <div className='welcome-msg'>
                  <WrappedMessage
                    message={authMessages.uiSidebarLoginMessage}
                  />
                </div>
                <div className='username'>{fullName}</div>
              </div>

              <NavLink
                to={ROUTES.Users.PROFILE_EDIT_SLUG(loggedInUsername)}
                className='profile-link'
                onClick={() => {
                  setSidebarOpen();
                }}
              >
                <WrappedMessage message={authMessages.uiMyProfile} />{' '}
                <Icon name='arrow-right-small-pointer' />
              </NavLink>
            </div>
          </div>
        )}

        <div className='mobile-sidebar-content'>
          <div className='mobile-menu-items'>
            <NavLink
              to={ROUTES.CareerExplorer.CAREER_HOME_URL}
              className='menu-option'
            >
              <Icon zoom='24' name='compass' />
              <WrappedMessage message={messages.exploreCareersNavOption} />
            </NavLink>
            <NavLink
              to={ROUTES.CareerExplorer.FIELD_HOME_URL}
              className='menu-option'
            >
              <Icon zoom='24' name='entry-level-jobs' />
              <WrappedMessage message={messages.exploreFieldsNavOption} />
            </NavLink>
          </div>

          {loggedInUsername && (
            <>
              <div className='mobile-menu-items'>
                <NavLink
                  to={ROUTES.CareerExplorer.SAVED_CAREERS}
                  className='menu-option'
                >
                  <Icon zoom='24' name='bookmark' />
                  <WrappedMessage message={messages.myCareerNavOption} />
                </NavLink>
              </div>
              <div className='mobile-menu-items'>
                <NavLink
                  to='#'
                  className='menu-option'
                  onClick={() => {
                    logout();
                  }}
                >
                  <Icon zoom='24' name='logout' />
                  <WrappedMessage message={authMessages.uiLogOut} />
                </NavLink>
              </div>
            </>
          )}
          {!loggedInUsername && (
            <div className='career-logged-out-menu'>
              <Button
                className='login-btn'
                btnStyle='outline'
                onClick={redirectToLogin}
                label={authMessages.uiSignIn}
              />
              <Button
                className='signup-btn'
                btnStyle='primary'
                onClick={redirectToLxSignUp}
                label={authMessages.uiSignUp}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileNavbar;
